import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  ContentCut as ContentCutIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Navigate, useNavigate } from 'react-router-dom';
import { surface } from '../theme';
import AuthService from '../services/AuthService';
import VideoFootageService from '../services/VideoFootageService';
import { useUploadContext } from '../contexts/UploadContext';
import FootageFolderTagsSelect from '../components/FootageFolderTagsSelect';

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running']);
const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

function isVideoFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('video/')) return true;
  return /\.(mp4|mov|webm|mkv|avi|m4v|wmv|flv|ogv|mpeg|mpg)$/i.test(file.name || '');
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function makeFileId(file) {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
}

function uploadToPresignedUrl({ presignedUrl, file, contentType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'video/mp4');
    xhr.upload.onprogress = (evt) => {
      const total = evt.lengthComputable && evt.total ? evt.total : file.size;
      onProgress({ loaded: evt.loaded, total });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress({ loaded: file.size, total: file.size });
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

function statusLabel(item) {
  if (item.error) return 'Failed';
  if (item.job && TERMINAL_JOB_STATUSES.has(item.job.status)) {
    if (item.job.status === 'completed') return 'Complete';
    if (item.job.status === 'completed_with_errors') return 'Completed with errors';
    return 'Failed';
  }
  if (item.job && ACTIVE_JOB_STATUSES.has(item.job.status)) return 'Cutting';
  if (item.status === 'uploading') return 'Uploading';
  if (item.status === 'uploaded') return 'Uploaded';
  if (item.status === 'starting') return 'Starting';
  return 'Ready';
}

export default function VideoFootageCutPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const folderSearchTimerRef = useRef(null);
  const dragDepthRef = useRef(0);
  const { setIsUploading } = useUploadContext();

  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderTags, setNewFolderTags] = useState([]);

  const [items, setItems] = useState([]);
  const [applyFadeInFadeOut, setApplyFadeInFadeOut] = useState(true);
  const [compressOutput, setCompressOutput] = useState(false);
  const [startingCuts, setStartingCuts] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [targetFolder, setTargetFolder] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const loadFolders = useCallback(async (search = '') => {
    try {
      setFoldersLoading(true);
      const data = await VideoFootageService.listFolders({
        all: true,
        search: search.trim() || undefined,
      });
      setFolders(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load folders');
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolders('');
  }, [loadFolders]);

  useEffect(() => {
    if (folderSearchTimerRef.current) clearTimeout(folderSearchTimerRef.current);
    folderSearchTimerRef.current = setTimeout(() => loadFolders(folderSearch), 300);
    return () => {
      if (folderSearchTimerRef.current) clearTimeout(folderSearchTimerRef.current);
    };
  }, [folderSearch, loadFolders]);

  const uploadSourceForItem = useCallback(async (item) => {
    try {
      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                status: 'uploading',
                uploadProgress: { loaded: 0, total: item.file.size },
                error: null,
              }
            : row,
        ),
      );

      const uploadTarget = await VideoFootageService.requestCutSourcePresignedUrl({
        fileName: item.file.name,
        fileSize: item.file.size,
        contentType: item.file.type || 'video/mp4',
      });

      await uploadToPresignedUrl({
        presignedUrl: uploadTarget.presignedUrl,
        file: item.file,
        contentType: uploadTarget.contentType || item.file.type || 'video/mp4',
        onProgress: (progress) => {
          setItems((prev) =>
            prev.map((row) =>
              row.id === item.id ? { ...row, uploadProgress: progress } : row,
            ),
          );
        },
      });

      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                status: 'uploaded',
                uploadTarget,
                uploadProgress: { loaded: item.file.size, total: item.file.size },
              }
            : row,
        ),
      );
    } catch (err) {
      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                status: 'failed',
                error:
                  err.response?.data?.error ||
                  err.message ||
                  'Failed to upload source video',
              }
            : row,
        ),
      );
    }
  }, []);

  const addFiles = useCallback(
    (fileList) => {
      const selected = Array.from(fileList || []);
      if (!selected.length) return;

      const videoFiles = selected.filter(isVideoFile);
      const skipped = selected.length - videoFiles.length;
      if (skipped > 0) {
        setInfo(`Skipped ${skipped} non-video file${skipped === 1 ? '' : 's'}.`);
      }
      if (!videoFiles.length) {
        setError('No video files found.');
        return;
      }

      const newItems = videoFiles.map((file) => ({
        id: makeFileId(file),
        file,
        status: 'ready',
        uploadProgress: { loaded: 0, total: file.size },
        uploadTarget: null,
        job: null,
        error: null,
      }));

      setError(null);
      setItems((prev) => [...prev, ...newItems]);
      newItems.forEach((item) => {
        uploadSourceForItem(item);
      });
    },
    [uploadSourceForItem],
  );

  useEffect(() => {
    const hasUploading = items.some((item) => item.status === 'uploading');
    if (setIsUploading) setIsUploading(hasUploading);
    return () => {
      if (setIsUploading) setIsUploading(false);
    };
  }, [items, setIsUploading]);

  useEffect(() => {
    const activeJobIds = items
      .filter((item) => item.job && ACTIVE_JOB_STATUSES.has(item.job.status))
      .map((item) => item.job.id)
      .filter((id, index, arr) => id && arr.indexOf(id) === index);

    if (!activeJobIds.length) return undefined;

    const timer = window.setInterval(async () => {
      const results = await Promise.allSettled(
        activeJobIds.map((jobId) => VideoFootageService.getCutJob(jobId)),
      );

      const jobsById = new Map();
      let firstError = null;
      for (const result of results) {
        if (result.status === 'fulfilled') {
          jobsById.set(result.value.id, result.value);
        } else if (!firstError) {
          firstError =
            result.reason?.response?.data?.error ||
            result.reason?.message ||
            'Failed to load cut progress';
        }
      }

      if (firstError) setError(firstError);
      if (jobsById.size === 0) return;

      setItems((prev) =>
        prev.map((item) => {
          const nextJob = item.job?.id ? jobsById.get(item.job.id) : null;
          if (!nextJob) return item;
          return {
            ...item,
            job: nextJob,
            status: TERMINAL_JOB_STATUSES.has(nextJob.status) ? 'done' : 'cutting',
            error:
              nextJob.status === 'failed'
                ? nextJob.failures?.[0]?.error || 'Cut job failed'
                : item.error,
          };
        }),
      );
    }, 2000);

    return () => window.clearInterval(timer);
  }, [items]);

  const handleFileChange = (event) => {
    const selected = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    addFiles(selected);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const onDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    addFiles(event.dataTransfer?.files || []);
  };

  const handleStartCut = async () => {
    const folderName = newFolderName.trim();
    const hasExistingFolder = Boolean(selectedFolder?.id);
    const hasNewFolderName = Boolean(folderName);
    const uploadsActive = items.some((item) => item.status === 'uploading');
    const eligibleItems = items.filter(
      (item) => item.status === 'uploaded' && item.uploadTarget?.objectName,
    );

    if (hasExistingFolder === hasNewFolderName) {
      setError('Choose either an existing folder or enter a new folder name.');
      return;
    }
    if (!items.length) {
      setError('Add at least one video.');
      return;
    }
    if (uploadsActive) {
      setError('Wait for source uploads to finish before cutting.');
      return;
    }
    if (!eligibleItems.length) {
      setError('No uploaded videos are ready to cut.');
      return;
    }

    try {
      setStartingCuts(true);
      setError(null);
      setInfo(null);

      let destinationFolder = selectedFolder;
      if (hasNewFolderName) {
        destinationFolder = await VideoFootageService.createFolder({
          name: folderName,
          tags: newFolderTags,
        });
        setSelectedFolder(destinationFolder);
        setNewFolderName('');
        setNewFolderTags([]);
        setTargetFolder(destinationFolder);
        await loadFolders('');
      } else {
        setTargetFolder(destinationFolder);
      }

      for (const item of eligibleItems) {
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, status: 'starting', error: null } : row,
          ),
        );

        try {
          const startedJob = await VideoFootageService.startCutJob({
            folderId: destinationFolder.id,
            sourceObjectName: item.uploadTarget.objectName,
            fileName: item.file.name,
            applyFadeInFadeOut,
            compressOutput,
          });

          setItems((prev) =>
            prev.map((row) =>
              row.id === item.id
                ? {
                    ...row,
                    status: ACTIVE_JOB_STATUSES.has(startedJob.status) ? 'cutting' : 'done',
                    job: startedJob,
                  }
                : row,
            ),
          );
        } catch (err) {
          setItems((prev) =>
            prev.map((row) =>
              row.id === item.id
                ? {
                    ...row,
                    status: 'failed',
                    error:
                      err.response?.data?.error ||
                      err.message ||
                      'Failed to start cut job',
                  }
                : row,
            ),
          );
        }
      }

      setInfo('Cut jobs started. Processing will continue on the server.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start cutting');
    } finally {
      setStartingCuts(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  const folderName = newFolderName.trim();
  const hasExistingFolder = Boolean(selectedFolder?.id);
  const hasNewFolderName = Boolean(folderName);
  const hasValidDestination = hasExistingFolder !== hasNewFolderName;
  const uploadsActive = items.some((item) => item.status === 'uploading');
  const anyActiveJob = items.some((item) => item.job && ACTIVE_JOB_STATUSES.has(item.job.status));
  const busy = startingCuts || anyActiveJob;
  const eligibleCount = items.filter(
    (item) => item.status === 'uploaded' && item.uploadTarget?.objectName,
  ).length;
  const finishedItems = items.filter(
    (item) => item.job && TERMINAL_JOB_STATUSES.has(item.job.status),
  ).length;
  const totalUploadBytes = items.reduce((sum, item) => sum + item.file.size, 0);
  const uploadedBytes = items.reduce(
    (sum, item) => sum + Math.min(item.uploadProgress?.loaded || 0, item.file.size),
    0,
  );
  const uploadPct = totalUploadBytes > 0 ? Math.min(100, (uploadedBytes / totalUploadBytes) * 100) : 0;
  const canOpenFolder = Boolean((targetFolder || selectedFolder)?.id);

  let destinationHelperText = 'Ready.';
  if (hasExistingFolder && hasNewFolderName) {
    destinationHelperText = 'Use one destination: clear the selected folder or clear the new folder name.';
  } else if (!hasExistingFolder && !hasNewFolderName) {
    destinationHelperText = 'Select an existing folder or enter a new folder name.';
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/video-footage')}
          sx={{ color: 'white', textTransform: 'none' }}
        >
          Back
        </Button>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          Cut Footage
        </Typography>
      </Box>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3, display: 'grid', gap: 3 }}>
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity='info' onClose={() => setInfo(null)}>
              {info}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Source videos
            </Typography>
            <input
              ref={fileInputRef}
              type='file'
              accept='video/*'
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Box
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              sx={{
                border: 1,
                borderStyle: 'dashed',
                borderColor: dragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: dragActive ? alpha('#1976d2', 0.08) : alpha('#fff', 0.35),
                p: 3,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  Drop videos here
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Upload starts immediately. Click Cut footage after uploads finish.
                </Typography>
              </Box>
              <Button
                variant='outlined'
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Select videos
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Footage folder
            </Typography>
            <Autocomplete
              options={folders}
              loading={foldersLoading}
              value={selectedFolder}
              onChange={(_, value) => {
                setSelectedFolder(value);
                if (value) setNewFolderName('');
              }}
              inputValue={folderSearch}
              onInputChange={(_, value) => setFolderSearch(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={busy || hasNewFolderName}
              ListboxProps={{
                style: { maxHeight: 320, overflowY: 'auto' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Select existing folder'
                  placeholder='Search folders'
                  helperText={hasNewFolderName ? 'Clear the new folder name to select an existing folder.' : ''}
                />
              )}
            />
            <TextField
              label='New folder name'
              value={newFolderName}
              onChange={(event) => {
                setNewFolderName(event.target.value);
                if (event.target.value.trim()) setSelectedFolder(null);
              }}
              disabled={busy || hasExistingFolder}
              helperText={destinationHelperText}
              error={!hasValidDestination && (hasExistingFolder || hasNewFolderName)}
            />
            {!hasExistingFolder && (
              <FootageFolderTagsSelect
                value={newFolderTags}
                onChange={setNewFolderTags}
                disabled={busy}
                margin='none'
              />
            )}
          </Box>

          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyFadeInFadeOut}
                  onChange={(event) => setApplyFadeInFadeOut(event.target.checked)}
                  disabled={busy}
                />
              }
              label='Apply 1s fade in and fade out'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={compressOutput}
                  onChange={(event) => setCompressOutput(event.target.checked)}
                  disabled={busy}
                />
              }
              label='Compress cut footage to about 1.5 GB per hour'
            />
          </Box>

          {items.length > 0 && (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  Upload progress
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {formatFileSize(uploadedBytes)} / {formatFileSize(totalUploadBytes)}
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={uploadPct}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant='body2' color='text.secondary'>
                {eligibleCount} uploaded, {finishedItems} finished cutting.
              </Typography>
            </Box>
          )}

          {items.length > 0 && (
            <Box sx={{ display: 'grid', gap: 1, borderTop: 1, borderColor: 'divider', pt: 1 }}>
              {items.map((item) => {
                const itemUploadPct =
                  item.file.size > 0
                    ? Math.min(100, ((item.uploadProgress?.loaded || 0) / item.file.size) * 100)
                    : 0;
                const jobPct =
                  item.job?.total > 0
                    ? Math.min(100, ((item.job.completed || 0) / item.job.total) * 100)
                    : 0;
                const activeCut = item.job && ACTIVE_JOB_STATUSES.has(item.job.status);

                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'grid',
                      gap: 1,
                      py: 1.25,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 700 }} noWrap title={item.file.name}>
                          {item.file.name}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {formatFileSize(item.file.size)}
                        </Typography>
                      </Box>
                      <Chip
                        size='small'
                        color={item.error ? 'error' : activeCut ? 'primary' : 'default'}
                        label={statusLabel(item)}
                      />
                      {!busy && item.status !== 'uploading' && (
                        <IconButton size='small' onClick={() => removeItem(item.id)}>
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      )}
                    </Box>
                    {(item.status === 'uploading' || itemUploadPct > 0) && (
                      <Box>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                          Upload: {formatFileSize(item.uploadProgress?.loaded || 0)} /{' '}
                          {formatFileSize(item.file.size)}
                        </Typography>
                        <LinearProgress
                          variant='determinate'
                          value={itemUploadPct}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    )}
                    {item.job && (
                      <Box>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                          Clips: {item.job.completed || 0} of {item.job.total || 0}
                          {item.job.failed > 0 ? ` (${item.job.failed} failed)` : ''}
                        </Typography>
                        <LinearProgress
                          variant={item.job.total > 0 ? 'determinate' : 'indeterminate'}
                          value={jobPct}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    )}
                    {item.error && (
                      <Typography variant='body2' color='error'>
                        {item.error}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant='contained'
              size='large'
              startIcon={startingCuts ? <CircularProgress size={20} color='inherit' /> : <ContentCutIcon />}
              onClick={handleStartCut}
              disabled={startingCuts || uploadsActive || !eligibleCount || !hasValidDestination}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Cut footage
            </Button>
            {canOpenFolder && (
              <Button
                variant='outlined'
                onClick={() => navigate(`/video-footage/${(targetFolder || selectedFolder).id}`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Open folder
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
